import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { signIn, AdminApiError } from '../api/admin.js';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = ((location.state as LocationState | null)?.from?.pathname ?? '/').toString();

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      await queryClient.invalidateQueries({ queryKey: ['admin-session'] });
      void navigate(from === '/login' ? '/' : from, { replace: true });
    } catch (err) {
      if (err instanceof AdminApiError) {
        if (err.status === 401 || err.code === 'invalid_credentials') {
          setError('Credenciales inválidas');
        } else if (err.status === 429) {
          setError('Demasiados intentos. Esperá un momento.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-screen" data-testid="login-screen">
      <div className="login-card">
        <div className="login-brand">
          <span className="sidebar-brand-dot" />
          <span>Orborix · Backoffice</span>
        </div>
        <form
          className="login-form"
          onSubmit={(e) => {
            void onSubmit(e);
          }}
        >
          <label>
            <span>Email</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
          </label>
          {error ? <div className="login-error" data-testid="login-error">{error}</div> : null}
          <button
            className="btn primary"
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
          >
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
          <p className="tt-tert" style={{ fontSize: 'var(--fs-xs)', margin: 0 }}>
            Acceso restringido a operadores. Las credenciales son las mismas que la app principal —
            tu cuenta debe tener rol admin o support.
          </p>
        </form>
      </div>
    </div>
  );
}
